import { Link, useLocation } from 'react-router-dom';
import { Home, FolderPlus, Dumbbell, BookOpen, LogOut } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';

const navItems = [
  { name: 'Home', href: '/dashboard', icon: Home },
  { name: 'Collection', href: '/collection', icon: FolderPlus },
  { name: 'Practice', href: '/practice', icon: Dumbbell },
];

function Header() {
  const location = useLocation();
  const { user, logout } = useAuth();

  // Compute active tab directly from current location
  const activeTab = navItems.find(item => {
    // Special handling for practice routes
    if (item.name === 'Practice') {
      return location.pathname.startsWith('/practice');
    }
    return item.href === location.pathname;
  })?.name || 'Home';

  const handleLogout = async () => {
    try {
      await logout();
      // Small delay to ensure logout completes before redirect
      // This prevents 401 redirects from interfering
      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    } catch (error) {
      console.error('Logout failed:', error);
      // Still redirect even if logout fails
      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-sm">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-2xl bg-primary">
            <BookOpen className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="hidden sm:inline text-lg font-semibold text-foreground">Vocabbit</span>
        </Link>

        <nav className="flex items-center gap-1 rounded-full bg-muted p-1">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = activeTab === item.name
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all",
                  isActive
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{item.name}</span>
              </Link>
            )
          })}
        </nav>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
              <Avatar className="h-9 w-9 border-2 border-primary/20 cursor-pointer transition-opacity hover:opacity-80">
                <AvatarFallback className="bg-primary/10 text-primary font-medium">
                  {user?.email?.[0]?.toUpperCase() || 'J'}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <p className="text-sm font-medium">{user?.email?.split('@')[0] || 'User'}</p>
              <p className="text-xs text-muted-foreground">{user?.email || 'user@example.com'}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="cursor-pointer focus:bg-muted">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export default Header;
